import React, { useEffect, useState, useCallback } from 'react';
import {
  resetAllData, getConnections, getTradingStatus, postKillSwitch, postFlatten, patchSettings,
  getContractMode, patchContractMode, getTradovateHealth, getHealth, getRiskLog,
} from '../api';
import { RefreshCw, ShieldCheck, ShieldAlert, ShieldOff, Power, Zap, FileText, Activity, AlertTriangle } from 'lucide-react';
import { formatTradeTime } from '../utils/time';
import { pnlColorClass } from '../utils/pnl';
import { useTheme } from '../context/ThemeContext';
import { useDensity } from '../context/DensityContext';
import { PageHeader } from './ui/PageHeader';
import { SectionHeader } from './ui/SectionHeader';

type ConnStatus = 'ok' | 'degraded' | 'error' | 'unknown';
type ConnRow = {
  status: ConnStatus;
  label: string;
  response_ms?: number;
  last_checked?: string;
  degraded?: boolean;
};
type ConnMap = Record<string, ConnRow>;

const SOURCE_ORDER: Array<{ key: string; title: string }> = [
  { key: 'api', title: 'Breaker API' },
  { key: 'tradovate', title: 'Tradovate' },
  { key: 'supabase', title: 'Supabase' },
  { key: 'tradingview', title: 'TradingView Webhook' },
  { key: 'news', title: 'News / Blackout' },
];

type Auth = 'public' | 'jwt' | 'webhook';
const ENDPOINT_ROUTES: Array<{ method: string; path: string; auth: Auth }> = [
  { method: 'GET', path: '/health', auth: 'public' },
  { method: 'POST', path: '/webhook/tradingview', auth: 'webhook' },
  { method: 'GET', path: '/signals', auth: 'jwt' },
  { method: 'GET', path: '/trades', auth: 'jwt' },
  { method: 'GET', path: '/positions', auth: 'jwt' },
  { method: 'GET', path: '/positions/live', auth: 'jwt' },
  { method: 'GET', path: '/pnl/daily', auth: 'jwt' },
  { method: 'GET', path: '/risk/log', auth: 'jwt' },
  { method: 'GET', path: '/audit/log', auth: 'jwt' },
  { method: 'GET', path: '/audit/misses', auth: 'jwt' },
  { method: 'GET', path: '/audit/deviations', auth: 'jwt' },
  { method: 'GET', path: '/cells', auth: 'jwt' },
  { method: 'POST', path: '/cells/{id}/approve', auth: 'jwt' },
  { method: 'POST', path: '/cells/{id}/pause', auth: 'jwt' },
  { method: 'POST', path: '/cells/{id}/resume', auth: 'jwt' },
  { method: 'POST', path: '/cells/{id}/scale', auth: 'jwt' },
  { method: 'POST', path: '/cells/recompute', auth: 'jwt' },
  { method: 'GET', path: '/withdrawals/recommendation', auth: 'jwt' },
  { method: 'GET', path: '/withdrawals/history', auth: 'jwt' },
  { method: 'POST', path: '/withdrawals/log', auth: 'jwt' },
  { method: 'GET', path: '/tradovate/health', auth: 'jwt' },
  { method: 'GET', path: '/review/summary', auth: 'jwt' },
  { method: 'GET', path: '/news/upcoming', auth: 'jwt' },
  { method: 'GET', path: '/connections', auth: 'jwt' },
  { method: 'GET', path: '/trading/status', auth: 'jwt' },
  { method: 'POST', path: '/trading/kill-switch', auth: 'jwt' },
  { method: 'POST', path: '/trading/autoexecute', auth: 'jwt' },
  { method: 'POST', path: '/trading/flatten', auth: 'jwt' },
  { method: 'POST', path: '/admin/reset', auth: 'jwt' },
];

export default function Settings() {
  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        title="Settings"
        description="System configuration and trading controls. Kill switch, flatten, and API connection status."
      />
      <div className="space-y-6">
        <ThemePanel />
        <ConnectionStatusPanel />
        <RiskLimitsPanel />
        <TradingControlsPanel />
        <ScoreThresholdsPanel />
        <ContractModePanel />
        <ConnectionsPanel />
        <EndpointSecurityPanel />
        <DangerZone />
      </div>
    </div>
  );
}

function ThemePanel() {
  const { theme, toggleTheme } = useTheme();
  const { compact, toggleCompact } = useDensity();
  return (
    <div className="bg-gray-900 rounded-lg border border-gray-800 p-5 space-y-4">
      <SectionHeader
        title="Theme"
        description={`Currently: ${theme === 'dark' ? 'Dark' : 'Light'} mode`}
        action={
          <button
            onClick={toggleTheme}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-800 hover:bg-gray-700 text-sm text-white transition-colors"
          >
            {theme === 'dark' ? '☀️ Switch to Light Mode' : '🌙 Switch to Dark Mode'}
          </button>
        }
      />
      <div className="flex items-center justify-between pt-4 border-t border-gray-800">
        <div>
          <p className="text-sm font-medium text-white">Compact Mode</p>
          <p className="text-xs text-gray-400 mt-0.5">Denser rows and cards for active monitoring</p>
        </div>
        <button
          onClick={toggleCompact}
          className="px-3 py-1.5 rounded-md bg-gray-800 hover:bg-gray-700 text-sm text-white transition-colors"
        >
          {compact ? 'On' : 'Off'}
        </button>
      </div>
    </div>
  );
}

function ConnectionStatusPanel() {
  const [health, setHealth] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await getTradovateHealth();
        setHealth(r.data);
        setError(null);
      } catch (e: any) {
        setError(e?.message || 'Failed to load Tradovate health');
        setHealth({ connected: false, last_checked: new Date().toISOString() });
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  if (!health) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
        <h3 className="font-semibold text-white flex items-center gap-2"><Activity size={15} /> Connection Status</h3>
        <p className="text-xs text-gray-500 mt-2">Loading...</p>
      </div>
    );
  }

  const connected = !!health.connected;
  const env = (health.env || 'demo').toLowerCase();
  const isLive = env === 'live';
  const envLabel = env.charAt(0).toUpperCase() + env.slice(1);
  const realized = Number(health.realized_pnl || 0);
  const open = Number(health.open_pnl || 0);
  const balance = Number(health.cash_balance || 0);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-5 space-y-3">
      <SectionHeader
        title={<span className="inline-flex items-center gap-2"><Activity size={15} /> {isLive ? 'Tradovate Live Connection' : 'Tradovate Demo Connection'}</span>}
        action={
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-red-400'}`} />
            <span className={`text-xs ${connected ? 'text-green-400' : 'text-red-400'}`}>
              {connected ? `Connected to ${envLabel}` : 'Disconnected'}
            </span>
            {connected && (
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full uppercase tracking-wider ${
                isLive ? 'bg-green-900 text-green-300 border border-green-700' : 'bg-gray-800 text-gray-400 border border-gray-700'
              }`}>
                {isLive ? 'Live' : 'Demo'}
              </span>
            )}
          </div>
        }
      />
      {error && <div className="text-xs text-red-400">{error}</div>}
      {health.account_name && (
        <div className="text-xs text-gray-500">Account: {health.account_name}</div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-xs text-gray-500 uppercase">{isLive ? 'Live Balance' : 'Demo Balance'}</div>
          <div className="text-2xl font-bold font-mono text-white">
            {balance > 0 ? `$${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '--'}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Realized P&L</div>
          <div className={`text-lg font-bold font-mono ${pnlColorClass(realized)}`}>${realized.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Open P&L</div>
          <div className={`text-lg font-bold font-mono ${pnlColorClass(open)}`}>${open.toFixed(2)}</div>
        </div>
      </div>
      <div className="text-xs text-gray-500">Last checked {formatTradeTime(health.last_checked)}</div>
    </div>
  );
}

function RiskLimitsPanel() {
  const [riskLog, setRiskLog] = useState<any[]>([]);
  const [health, setHealth] = useState<any>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [r, h] = await Promise.all([getRiskLog(), getHealth()]);
        setRiskLog(r.data || []);
        setHealth(h.data);
      } catch (e) { console.error(e); }
    };
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const dailyLimit = riskLog[0]?.daily_loss_limit || 500;
  const startingBalance = health?.account_starting_balance || 0;
  const limitPct = startingBalance > 0 ? (dailyLimit / startingBalance) * 100 : null;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-5 space-y-3">
      <SectionHeader title={<span className="inline-flex items-center gap-2"><AlertTriangle size={15} /> Risk Limits</span>} />
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-xs text-gray-500 uppercase">Daily Loss Limit</div>
          <div className="text-2xl font-bold font-mono text-white">${dailyLimit.toFixed(0)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Starting Balance</div>
          <div className="text-2xl font-bold font-mono text-white">${startingBalance.toFixed(0)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Limit % of Start</div>
          <div className="text-2xl font-bold font-mono text-blue-400">
            {limitPct !== null ? `${limitPct.toFixed(1)}%` : '--'}
          </div>
        </div>
      </div>
    </div>
  );
}

function TradingControlsPanel() {
  const [killSwitch, setKillSwitch] = useState<boolean | null>(null);
  const [ksLoading, setKsLoading] = useState(false);
  const [showFlatten, setShowFlatten] = useState(false);
  const [flattenText, setFlattenText] = useState('');
  const [flattenStatus, setFlattenStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [flattenMsg, setFlattenMsg] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Accept either `kill_switch` or `halted` -- /trading/status returns both,
  // but older builds of /trading/kill-switch only returned `halted`.
  const readKillSwitch = (data: any): boolean =>
    !!(data?.kill_switch ?? data?.halted);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await getTradingStatus();
      setKillSwitch(readKillSwitch(r.data));
    } catch (e: any) {
      setError(e?.message || 'Failed to load trading status');
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const toggleKillSwitch = async () => {
    setKsLoading(true);
    setError(null);
    try {
      const r = await postKillSwitch();
      setKillSwitch(readKillSwitch(r.data));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to toggle kill switch');
    } finally {
      setKsLoading(false);
    }
  };

  const handleFlatten = async () => {
    if (flattenText !== 'FLATTEN') return;
    setFlattenStatus('loading');
    try {
      await postFlatten();
      setFlattenStatus('success');
      setFlattenMsg('All positions flattened.');
      setShowFlatten(false);
      setFlattenText('');
    } catch (e: any) {
      setFlattenStatus('error');
      setFlattenMsg(e?.response?.data?.detail || 'Flatten failed');
    }
  };

  const ksBg = killSwitch === true
    ? 'bg-red-600 hover:bg-red-500 text-white border-red-500'
    : 'bg-gray-700 hover:bg-gray-600 text-gray-300 border-gray-600';

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-5 space-y-4">
      <SectionHeader title={<span className="inline-flex items-center gap-2"><Power size={15} /> Trading Controls</span>} />
      {error && <div className="bg-red-900/30 border border-red-700 rounded p-2 text-red-400 text-xs">{error}</div>}
      {flattenStatus === 'success' && <div className="bg-green-900/30 border border-green-700 rounded p-2 text-green-400 text-xs">{flattenMsg}</div>}
      {flattenStatus === 'error' && <div className="bg-red-900/30 border border-red-700 rounded p-2 text-red-400 text-xs">{flattenMsg}</div>}

      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        {/* Kill Switch */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-300 font-medium">Kill Switch</span>
          <button
            onClick={toggleKillSwitch}
            disabled={ksLoading || killSwitch === null}
            className={`px-4 py-1.5 rounded border text-sm font-semibold transition-colors disabled:opacity-50 ${ksBg}`}
          >
            {ksLoading ? '...' : killSwitch === null ? 'Loading' : killSwitch ? 'ON — Trading Halted' : 'OFF — Trading Active'}
          </button>
          <span className="text-xs text-gray-500">{killSwitch ? 'No new orders will be sent.' : 'System will execute qualifying signals.'}</span>
        </div>
      </div>

      {/* Flatten */}
      <div className="border-t border-gray-800 pt-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-300 font-medium">Flatten All Trades</span>
          {!showFlatten ? (
            <button
              onClick={() => { setShowFlatten(true); setFlattenStatus('idle'); }}
              className="px-3 py-1.5 rounded bg-red-800 hover:bg-red-700 text-white text-sm font-semibold transition-colors"
            >
              Flatten All
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={flattenText}
                onChange={(e) => setFlattenText(e.target.value)}
                placeholder="Type FLATTEN"
                className="bg-gray-700 text-white px-3 py-1.5 rounded w-36 text-sm border border-gray-600 focus:border-red-500 focus:outline-none"
              />
              <button
                onClick={handleFlatten}
                disabled={flattenText !== 'FLATTEN' || flattenStatus === 'loading'}
                className={`px-3 py-1.5 rounded text-sm font-semibold transition-colors ${flattenText === 'FLATTEN' ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}
              >
                {flattenStatus === 'loading' ? 'Flattening...' : 'Confirm'}
              </button>
              <button onClick={() => { setShowFlatten(false); setFlattenText(''); }} className="text-xs text-gray-400 hover:text-white">Cancel</button>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1.5">Immediately closes all open Tradovate positions at market. Requires typing FLATTEN to confirm.</p>
      </div>
    </div>
  );
}


function ContractModePanel() {
  type ContractInfo = {
    symbol: string | null;
    expiry: string | null;
    days_to_expiry: number | null;
    volume: number | null;
    open_interest: number | null;
    margin: number;
  };
  type ContractData = {
    active_mode: 'NQ' | 'MNQ';
    instrument_map: string;
    contracts: { NQ: ContractInfo; MNQ: ContractInfo };
    roll_warning: boolean;
    roll_threshold_days: number;
  };

  const [data, setData] = useState<ContractData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const r = await getContractMode();
      setData(r.data);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load contract mode');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const switchMode = async (mode: 'NQ' | 'MNQ') => {
    if (!data || data.active_mode === mode || switching) return;
    setSwitching(true);
    try {
      const r = await patchContractMode(mode);
      setData(r.data);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to switch mode');
    } finally {
      setSwitching(false);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '\u2014';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const fmt = (n: number | null | undefined) =>
    n != null ? n.toLocaleString() : '\u2014';

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
      <SectionHeader title={<span className="inline-flex items-center gap-2"><FileText className="w-4 h-4 text-gray-400" /> Contract Mode</span>} />

      {loading && (
        <div className="text-xs text-gray-500 animate-pulse">Loading contract data...</div>
      )}

      {!loading && error && (
        <div className="text-xs text-red-400">{error}</div>
      )}

      {!loading && !error && data && (
        <>
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => switchMode('MNQ')}
              disabled={switching}
              className={`px-4 py-2 rounded text-xs font-semibold transition-colors ${
                data.active_mode === 'MNQ'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              MNQ (Micro)
            </button>
            <button
              onClick={() => switchMode('NQ')}
              disabled={switching}
              className={`px-4 py-2 rounded text-xs font-semibold transition-colors ${
                data.active_mode === 'NQ'
                  ? 'bg-amber-500 text-black'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              NQ (Full-Size)
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            {data.active_mode === 'MNQ' ? '~$100 margin per contract' : '~$1,000 margin per contract'}
          </p>

          <div className="grid grid-cols-2 gap-3 mb-4">
            {(['NQ', 'MNQ'] as const).map((sym) => {
              const c = data.contracts[sym];
              const isActive = data.active_mode === sym;
              const rollWarn = c.days_to_expiry != null && c.days_to_expiry <= data.roll_threshold_days;
              return (
                <div
                  key={sym}
                  className={`rounded-lg p-4 border ${
                    isActive ? 'border-blue-500 bg-gray-800' : 'border-gray-700 bg-gray-800'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-sm font-bold text-white">{c.symbol || sym}</span>
                    <span className="text-xs bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">FRONT MONTH</span>
                    {rollWarn && (
                      <span className="text-xs bg-red-600 text-white px-1.5 py-0.5 rounded font-semibold">ROLL NOW</span>
                    )}
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Expiry</span>
                      <span className="text-gray-200">{formatDate(c.expiry)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Days left</span>
                      <span className={rollWarn ? 'text-red-400 font-semibold' : 'text-gray-200'}>
                        {c.days_to_expiry != null ? `${c.days_to_expiry} days` : '\u2014'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Volume</span>
                      <span className="text-gray-200">{fmt(c.volume)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Open Interest</span>
                      <span className="text-gray-200">{fmt(c.open_interest)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Margin</span>
                      <span className="text-gray-200">{`$${c.margin.toLocaleString()} / contract`}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-600">
            Roll warning triggers at {'\u2264'}{data.roll_threshold_days} days to expiry.
          </p>
        </>
      )}
    </div>
  );
}

function ScoreThresholdsPanel() {
  const [minAuto, setMinAuto] = useState<number | null>(null);
  const [macroLiq, setMacroLiq] = useState<number | null>(null);
  const [editing, setEditing] = useState({ minAuto: '', macroLiq: '' });
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveMsg, setSaveMsg] = useState('');
  const [smrBand, setSmrBand] = useState<{ blocked: boolean; low: number; high: number } | null>(null);

  const fetchThresholds = useCallback(async () => {
    try {
      const r = await getTradingStatus();
      const ma = r.data?.min_auto_execute_score ?? r.data?.MIN_AUTO_EXECUTE_SCORE ?? 40;
      const ml = r.data?.macro_liq_min_score ?? r.data?.MACRO_LIQ_MIN_SCORE ?? 40;
      setMinAuto(ma);
      setMacroLiq(ml);
      setEditing({ minAuto: String(ma), macroLiq: String(ml) });
      setSmrBand({
        blocked: r.data?.smr_score_band_blocked ?? true,
        low: r.data?.smr_score_band_low ?? 80,
        high: r.data?.smr_score_band_high ?? 85,
      });
    } catch {
      setMinAuto(40);
      setMacroLiq(40);
      setEditing({ minAuto: '40', macroLiq: '40' });
    }
  }, []);

  useEffect(() => { fetchThresholds(); }, [fetchThresholds]);

  const handleSave = async () => {
    const ma = parseInt(editing.minAuto, 10);
    const ml = parseInt(editing.macroLiq, 10);
    if (isNaN(ma) || isNaN(ml)) { setSaveStatus('error'); setSaveMsg('Invalid values'); return; }
    setSaveStatus('saving');
    try {
      await patchSettings({ min_auto_execute_score: ma, macro_liq_min_score: ml });
      setMinAuto(ma);
      setMacroLiq(ml);
      setSaveStatus('saved');
      setSaveMsg('Saved. Logged for analytics only -- does not change execution behavior.');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (e: any) {
      setSaveStatus('error');
      setSaveMsg(e?.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-5 space-y-4">
      <SectionHeader title={<span className="inline-flex items-center gap-2"><Zap size={15} /> Score Thresholds (informational)</span>} />
      <p className="text-[11px] text-gray-500 -mt-2">
        Retired as execution gates on 2026-07-10. Sizing/execution is now criteria-count based.
        These values are logged for analytics only and no longer block trades.
      </p>
      {smrBand && (
        <div className={`rounded p-2 text-xs border ${smrBand.blocked ? 'bg-red-900/20 border-red-700 text-red-300' : 'bg-gray-800 border-gray-700 text-gray-400'}`}>
          <strong>SMR score-band gate: {smrBand.blocked ? 'ACTIVE (still blocks trades)' : 'disabled'}.</strong>{' '}
          {smrBand.blocked
            ? `SMR-class signals scoring ${smrBand.low}-${smrBand.high} are rejected pending more live data.`
            : `Would reject SMR-class signals scoring ${smrBand.low}-${smrBand.high} if re-enabled.`}
        </div>
      )}
      {saveStatus === 'saved' && <div className="bg-green-900/30 border border-green-700 rounded p-2 text-green-400 text-xs">{saveMsg}</div>}
      {saveStatus === 'error' && <div className="bg-red-900/30 border border-red-700 rounded p-2 text-red-400 text-xs">{saveMsg}</div>}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Auto-Execute Minimum Score (log only)</label>
          <input
            type="number"
            min={0} max={100}
            value={editing.minAuto}
            onChange={(e) => setEditing((s) => ({ ...s, minAuto: e.target.value }))}
            className="bg-gray-800 border border-gray-700 text-white px-3 py-1.5 rounded w-24 text-sm focus:border-blue-500 focus:outline-none"
          />
          <p className="text-[11px] text-gray-500 mt-1">Effective: {minAuto ?? '...'}</p>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">MACRO_LIQ Floor (log only)</label>
          <input
            type="number"
            min={0} max={100}
            value={editing.macroLiq}
            onChange={(e) => setEditing((s) => ({ ...s, macroLiq: e.target.value }))}
            className="bg-gray-800 border border-gray-700 text-white px-3 py-1.5 rounded w-24 text-sm focus:border-blue-500 focus:outline-none"
          />
          <p className="text-[11px] text-gray-500 mt-1">Effective: {macroLiq ?? '...'}</p>
        </div>
      </div>
      <button
        onClick={handleSave}
        disabled={saveStatus === 'saving'}
        className="px-4 py-1.5 rounded bg-blue-700 hover:bg-blue-600 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-semibold transition-colors"
      >
        {saveStatus === 'saving' ? 'Saving...' : 'Save Thresholds'}
      </button>
    </div>
  );
}

function ConnectionsPanel() {
  const [conns, setConns] = useState<ConnMap>({});
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadingRow, setLoadingRow] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async (rowKey?: string) => {
    if (rowKey) setLoadingRow((s) => ({ ...s, [rowKey]: true }));
    else setLoadingAll(true);
    setError(null);
    try {
      const r = await getConnections();
      setConns(r.data || {});
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load');
    } finally {
      if (rowKey) setLoadingRow((s) => ({ ...s, [rowKey]: false }));
      else setLoadingAll(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-5 space-y-3">
      <SectionHeader
        title="API Connections"
        action={
          <button
            onClick={() => fetchAll()}
            disabled={loadingAll}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded bg-blue-700 hover:bg-blue-600 disabled:bg-gray-700 disabled:text-gray-500 text-white transition-colors"
          >
            <RefreshCw size={12} className={loadingAll ? 'animate-spin' : ''} />
            Re-check All
          </button>
        }
      />
      {error && (
        <div className="bg-red-900/30 border border-red-700 rounded p-2 text-red-400 text-xs">{error}</div>
      )}
      <div className="grid sm:grid-cols-2 gap-2">
        {SOURCE_ORDER.map(({ key, title }) => {
          const row = conns[key];
          const busy = !!loadingRow[key] || (loadingAll && !row);
          return (
            <ConnectionCard
              key={key}
              title={title}
              row={row}
              loading={busy}
              onRecheck={() => fetchAll(key)}
            />
          );
        })}
      </div>
    </div>
  );
}

function ConnectionCard({ title, row, loading, onRecheck }: { title: string; row?: ConnRow; loading: boolean; onRecheck: () => void; }) {
  const status: ConnStatus = row?.status ?? 'unknown';
  const statusCls = status === 'ok'
    ? 'bg-green-900/40 text-green-300 border-green-700'
    : status === 'degraded'
      ? 'bg-yellow-900/40 text-yellow-300 border-yellow-700'
      : status === 'error'
        ? 'bg-red-900/40 text-red-300 border-red-700'
        : 'bg-gray-800 text-gray-400 border-gray-700';
  const statusText = status === 'unknown' ? '—' : status.toUpperCase();
  const checkedAt = row?.last_checked ? new Date(row.last_checked).toLocaleTimeString() : '—';
  return (
    <div className="bg-gray-950 border border-gray-800 rounded p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-white">{title}</span>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${statusCls}`}>{statusText}</span>
      </div>
      <div className="text-xs text-gray-300 break-words min-h-[1.25rem]">{row?.label || (loading ? 'Checking…' : '—')}</div>
      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span>Last checked: {checkedAt}{row?.response_ms != null ? ` · ${row.response_ms}ms` : ''}</span>
        <button
          onClick={onRecheck}
          disabled={loading}
          className="flex items-center gap-1 px-2 py-0.5 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-300 transition-colors"
        >
          <RefreshCw size={10} className={loading ? 'animate-spin' : ''} />
          Re-check
        </button>
      </div>
    </div>
  );
}

function EndpointSecurityPanel() {
  const groups: Array<{ auth: Auth; title: string; cls: string; Icon: typeof ShieldCheck }> = [
    { auth: 'public', title: 'Public', cls: 'text-yellow-400', Icon: ShieldOff },
    { auth: 'webhook', title: 'Webhook secret', cls: 'text-blue-400', Icon: ShieldAlert },
    { auth: 'jwt', title: 'Authenticated (Supabase JWT)', cls: 'text-green-400', Icon: ShieldCheck },
  ];
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-5 space-y-3">
      <SectionHeader
        title="Endpoint Security"
        description="All non-public routes are gated by the JWT middleware. Routes requiring the webhook secret accept it as an alternative for machine clients."
      />
      <div className="space-y-3">
        {groups.map(({ auth, title, cls, Icon }) => {
          const rows = ENDPOINT_ROUTES.filter((r) => r.auth === auth);
          if (rows.length === 0) return null;
          return (
            <div key={auth}>
              <div className={`flex items-center gap-1.5 text-xs font-semibold mb-1 ${cls}`}>
                <Icon size={12} />
                <span>{title}</span>
                <span className="text-gray-500 font-normal">({rows.length})</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-1">
                {rows.map((r) => (
                  <div key={`${r.method}-${r.path}`} className="flex items-center gap-2 bg-gray-950 border border-gray-800 rounded px-2 py-1 font-mono text-[11px]">
                    <span className={`px-1 rounded text-[10px] font-bold ${r.method === 'GET' ? 'bg-blue-900 text-blue-300' : 'bg-purple-900 text-purple-300'}`}>{r.method}</span>
                    <span className="text-gray-300">{r.path}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DangerZone() {
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleReset = async () => {
    if (confirmText !== 'RESET') return;
    setStatus('loading');
    try {
      await resetAllData();
      setStatus('success');
      setMessage('All trading data cleared. Refresh the dashboard.');
      setShowConfirm(false);
      setConfirmText('');
    } catch (err: any) {
      setStatus('error');
      setMessage(err?.response?.data?.detail || 'Reset failed');
    }
  };

  return (
    <div className="bg-gray-900 border border-red-800 rounded-lg p-6 space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-red-500 text-lg font-bold">DANGER ZONE</span>
      </div>

      <div>
        <h3 className="font-semibold text-white">Reset All Trading Data</h3>
        <p className="text-sm text-gray-400 mt-1">
          This will permanently delete all signals, trades, positions, P&L history,
          audit logs, risk logs, sequence state, HTF liquidity state, and withdrawals.
          Account balance returns to the configured starting balance. This cannot be undone.
        </p>
      </div>

      {status === 'success' && (
        <div className="bg-green-900/30 border border-green-700 rounded p-3 text-green-400 text-sm">{message}</div>
      )}
      {status === 'error' && (
        <div className="bg-red-900/30 border border-red-700 rounded p-3 text-red-400 text-sm">{message}</div>
      )}

      {!showConfirm ? (
        <button
          onClick={() => setShowConfirm(true)}
          className="bg-red-700 hover:bg-red-600 text-white px-4 py-2 rounded text-sm font-semibold transition-colors"
        >
          Reset All Data
        </button>
      ) : (
        <div className="space-y-3 bg-gray-800 p-4 rounded">
          <p className="text-sm text-yellow-400 font-semibold">Type RESET to confirm:</p>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type RESET"
            className="bg-gray-700 text-white px-3 py-2 rounded w-48 text-sm border border-gray-600 focus:border-red-500 focus:outline-none"
          />
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              disabled={confirmText !== 'RESET' || status === 'loading'}
              className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${
                confirmText === 'RESET'
                  ? 'bg-red-600 hover:bg-red-500 text-white'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {status === 'loading' ? 'Resetting...' : 'Confirm Reset'}
            </button>
            <button
              onClick={() => { setShowConfirm(false); setConfirmText(''); }}
              className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-4 py-2 rounded text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
